/**
 * Unsaved Changes Manager
 * 
 * Tracks unsaved changes across the application and provides
 * auto-save functionality to prevent data loss during session expiration.
 */

interface UnsavedData {
  componentId: string;
  componentName: string;
  data: any;
  timestamp: number;
  autoSaveEnabled: boolean;
}

type ChangeListener = (hasChanges: boolean, unsavedCount: number) => void;

class UnsavedChangesManager {
  private unsavedChanges: Map<string, UnsavedData> = new Map();
  private listeners: Set<ChangeListener> = new Set();
  private autoSaveInterval: NodeJS.Timeout | null = null;

  constructor() {
    // Load any previously saved drafts from localStorage
    this.loadDraftsFromStorage();
    
    // Start auto-save interval (every 30 seconds)
    this.startAutoSave();
    
    // Warn user before leaving page with unsaved changes
    this.setupBeforeUnloadWarning();
  }

  /**
   * Register a component with unsaved changes
   */
  registerUnsavedChanges(
    componentId: string,
    componentName: string,
    data: any,
    autoSaveEnabled: boolean = true
  ) {
    this.unsavedChanges.set(componentId, {
      componentId,
      componentName,
      data,
      timestamp: Date.now(),
      autoSaveEnabled,
    });

    this.notifyListeners();

    // Immediately save to localStorage if auto-save is enabled
    if (autoSaveEnabled) {
      this.saveDraftToStorage(componentId);
    }
  }

  /**
   * Clear unsaved changes for a specific component
   */
  clearUnsavedChanges(componentId: string) {
    this.unsavedChanges.delete(componentId);
    this.removeDraftFromStorage(componentId);
    this.notifyListeners();
  }

  /**
   * Clear all unsaved changes
   */
  clearAllUnsavedChanges() {
    this.unsavedChanges.clear();
    this.clearAllDraftsFromStorage();
    this.notifyListeners();
  }

  /**
   * Check if there are any unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.unsavedChanges.size > 0;
  }

  /**
   * Get count of unsaved changes
   */
  getUnsavedCount(): number {
    return this.unsavedChanges.size;
  }

  /**
   * Get all unsaved changes
   */
  getAllUnsavedChanges(): UnsavedData[] {
    return Array.from(this.unsavedChanges.values());
  }

  /**
   * Get unsaved changes for a specific component
   */
  getUnsavedChanges(componentId: string): UnsavedData | undefined {
    return this.unsavedChanges.get(componentId);
  }

  /**
   * Get draft from localStorage for a specific component
   */
  getDraftFromStorage(componentId: string): any | null {
    try {
      const draftKey = `scopify_draft_${componentId}`;
      const draft = localStorage.getItem(draftKey);
      if (draft) {
        const parsed = JSON.parse(draft);
        return parsed.data;
      }
    } catch (error) {
      console.error('Error loading draft from storage:', error);
    }
    return null;
  }

  /**
   * Subscribe to unsaved changes updates
   */
  subscribe(listener: ChangeListener) {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener(this.hasUnsavedChanges(), this.getUnsavedCount());
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Save all unsaved changes to localStorage as drafts
   */
  saveAllDraftsToStorage() {
    this.unsavedChanges.forEach((unsavedData, componentId) => {
      if (unsavedData.autoSaveEnabled) {
        this.saveDraftToStorage(componentId);
      }
    });
  }

  /**
   * Get list of all available drafts from localStorage
   */
  getAllDraftsFromStorage(): Array<{ componentId: string; componentName: string; timestamp: number; data: any }> {
    const drafts: Array<{ componentId: string; componentName: string; timestamp: number; data: any }> = [];
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('scopify_draft_')) {
          const draft = localStorage.getItem(key);
          if (draft) {
            const parsed = JSON.parse(draft);
            drafts.push({
              componentId: parsed.componentId,
              componentName: parsed.componentName,
              timestamp: parsed.timestamp,
              data: parsed.data,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading drafts from storage:', error);
    }

    // Sort by timestamp (newest first)
    return drafts.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Cleanup old drafts (older than 7 days)
   */
  cleanupOldDrafts() {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const drafts = this.getAllDraftsFromStorage();
    
    drafts.forEach(draft => {
      if (draft.timestamp < sevenDaysAgo) {
        this.removeDraftFromStorage(draft.componentId);
      }
    });
  }

  // Private methods

  private saveDraftToStorage(componentId: string) {
    const unsavedData = this.unsavedChanges.get(componentId);
    if (!unsavedData) return;

    try {
      const draftKey = `scopify_draft_${componentId}`;
      localStorage.setItem(draftKey, JSON.stringify({
        componentId: unsavedData.componentId,
        componentName: unsavedData.componentName,
        data: unsavedData.data,
        timestamp: unsavedData.timestamp,
      }));
    } catch (error) {
      console.error('Error saving draft to storage:', error);
    }
  }

  private removeDraftFromStorage(componentId: string) {
    try {
      const draftKey = `scopify_draft_${componentId}`;
      localStorage.removeItem(draftKey);
    } catch (error) {
      console.error('Error removing draft from storage:', error);
    }
  }

  private clearAllDraftsFromStorage() {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('scopify_draft_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing drafts from storage:', error);
    }
  }

  private loadDraftsFromStorage() {
    // Load drafts but don't automatically register them as unsaved changes
    // They will be offered to the user as recovery options
    this.cleanupOldDrafts();
  }

  private startAutoSave() {
    // Auto-save every 30 seconds
    this.autoSaveInterval = setInterval(() => {
      if (this.hasUnsavedChanges()) {
        console.log('Auto-saving unsaved changes to localStorage...');
        this.saveAllDraftsToStorage();
      }
    }, 30 * 1000);
  }

  private setupBeforeUnloadWarning() {
    window.addEventListener('beforeunload', (e) => {
      if (this.hasUnsavedChanges()) {
        e.preventDefault();
        // Modern browsers show a generic message, but we still need to set returnValue
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    });
  }

  private notifyListeners() {
    const hasChanges = this.hasUnsavedChanges();
    const count = this.getUnsavedCount();
    this.listeners.forEach(listener => listener(hasChanges, count));
  }

  destroy() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
  }
}

// Export singleton instance
export const unsavedChangesManager = new UnsavedChangesManager();
